import { Divider, FormControl, FormHelperText, Stack } from "@mui/material";
import React from "react";
import { SnackBar } from "../ui/snack-bar";
import { useTheme } from "@quales/utils/theme";
import { CustomTextField } from "../ui/input";

import { CustomTypographyComponent } from "../ui/text";
import { GQLTenantRegistration } from "@quales/data-access/graphql";

interface RegistrationFormProps {
  onRegistrationDetails: (val: RegistrationFormState) => void;
  isAdminScreen?: boolean;
  inputData?: GQLTenantRegistration;
}

export interface RegistrationFormState {
  emailAddress: string;
  organization: string;
  emailAddressInvoice: string;
  street: string;
  number: string;
  postalCode: string;
  city: string;
  phoneNumber: string;
  firstName: string;
  infix: string;
  reference: string;
  purchaseNumber: string;
  lastName: string;
  isTrial?: boolean;
}

interface RegistrationFormError {
  emailAddress: string;
  organization: string;
  emailAddressInvoice: string;
  street: String;
  number: String;
  postalCode: string;
  city: string;
  phoneNumber: String;
  firstName: String;
  lastName: String;
}

export const RegistrationForm = React.forwardRef(
  (props: RegistrationFormProps, ref) => {
    const { onRegistrationDetails, isAdminScreen, inputData } = props;
    const theme = useTheme();
    const [values, setValues] = React.useState<RegistrationFormState>({
      emailAddress: "",
      organization: "",
      emailAddressInvoice: "",
      street: "",
      number: "",
      postalCode: "",
      purchaseNumber: "",
      reference: "",
      city: "",
      phoneNumber: "",
      firstName: "",
      infix: "",
      isTrial: true,
      lastName: "",
    });
    const [formIsSubmitted, setFormIsSubmitted] = React.useState(false);
    const [snackBar, setSnackBar] = React.useState(false);
    const [snackBarMessage, setSnackBarMessage] = React.useState("");
    const [severity, setSeverity] = React.useState("");
    const [formErrors, setFormErrors] = React.useState<RegistrationFormError>({
      emailAddress: "Sommige verplichte velden zijn niet ingevuld",
      organization: "Sommige verplichte velden zijn niet ingevuld",
      emailAddressInvoice: "Sommige verplichte velden zijn niet ingevuld",
      street: "Sommige verplichte velden zijn niet ingevuld",
      number: "Sommige verplichte velden zijn niet ingevuld",
      postalCode: "Sommige verplichte velden zijn niet ingevuld",
      city: "Sommige verplichte velden zijn niet ingevuld",
      phoneNumber: "Sommige verplichte velden zijn niet ingevuld",
      firstName: "Sommige verplichte velden zijn niet ingevuld",
      lastName: "Sommige verplichte velden zijn niet ingevuld",
    });

    const checkRequiredValues = (val: RegistrationFormState) => {
      setFormErrors({
        city:
          val.city !== "" ? "" : "Sommige verplichte velden zijn niet ingevuld",
        emailAddress:
          val.emailAddress !== ""
            ? ""
            : "Sommige verplichte velden zijn niet ingevuld",
        emailAddressInvoice:
          val.emailAddressInvoice !== ""
            ? ""
            : "Sommige verplichte velden zijn niet ingevuld",
        firstName:
          val.firstName !== ""
            ? ""
            : "Sommige verplichte velden zijn niet ingevuld",
        lastName:
          val.lastName !== ""
            ? ""
            : "Sommige verplichte velden zijn niet ingevuld",
        number:
          val.number !== ""
            ? ""
            : "Sommige verplichte velden zijn niet ingevuld",
        organization:
          val.organization !== ""
            ? ""
            : "Sommige verplichte velden zijn niet ingevuld",
        phoneNumber:
          val.phoneNumber !== ""
            ? ""
            : "Sommige verplichte velden zijn niet ingevuld",
        postalCode:
          val.postalCode !== ""
            ? ""
            : "Sommige verplichte velden zijn niet ingevuld",
        street:
          val.street !== ""
            ? ""
            : "Sommige verplichte velden zijn niet ingevuld",
      });
    };

    React.useEffect(() => {
      if (isAdminScreen && inputData) {
        const tempOBJ = {
          emailAddress: inputData.adminEmailAddress || "",
          organization: (inputData.name && inputData.name) || "",
          emailAddressInvoice: inputData.emailAddressInvoice || "",
          street: inputData.street || "",
          number: inputData.houseNumber || "",
          postalCode: inputData.postalCode || "",
          purchaseNumber: inputData.purchaseNumber || "",
          reference: "",
          city: inputData.city || "",
          phoneNumber:
            (inputData.adminPhoneNumber && inputData.adminPhoneNumber) || "",
          firstName: inputData.adminFirstName || "",
          infix: inputData.adminInfix || "",
          isTrial: inputData.isTrial || false,
          lastName: inputData.adminLastName || "",
        };

        checkRequiredValues(tempOBJ);
        setValues({ ...tempOBJ });
      }
    }, [isAdminScreen, inputData]);

    const handleClose = (event?: React.SyntheticEvent, reason?: string) => {
      if (reason === "clickaway") {
        return;
      }

      setSnackBar(false);
    };
    const handleChange =
      (prop: keyof RegistrationFormState) =>
      (event: React.ChangeEvent<HTMLInputElement>) => {
        setValues({ ...values, [prop]: event.target.value });
        if (Object.keys(formErrors).includes(prop)) {
          if (event.target.value !== "") {
            formErrors[prop] = "";
          } else {
            formErrors[prop] = "Sommige verplichte velden zijn niet ingevuld";
          }
          setValues({ ...values, [prop]: event.target.value });
        } else {
          setValues({ ...values, [prop]: event.target.value });
        }
        setFormErrors({ ...formErrors });
      };

    const handleCheckBox = (val: React.ChangeEvent<HTMLInputElement>) => {
      setValues({ ...values, isTrial: val.target.checked });
    };

    React.useImperativeHandle(ref, () => ({
      onSubmit() {
        setFormIsSubmitted(true);
        if (Object.values(formErrors).some((item) => item.length > 0)) {
          setSnackBar(true);
          setSeverity("error");
          setSnackBarMessage("Sommige verplichte velden zijn niet ingevuld");
        } else {
          setFormErrors({
            emailAddress: "",
            organization: "",
            emailAddressInvoice: "",
            street: "",
            number: "",
            postalCode: "",
            city: "",
            phoneNumber: "",
            firstName: "",
            lastName: "",
          });
          onRegistrationDetails(values);
        }
      },
    }));

    return (
      <Stack width="98%" id="RegistrationForm" spacing={10} padding={2}>
        <Stack>
          {!inputData && (
            <CustomTypographyComponent variant="h5">
              Registratie nieuwe klant
            </CustomTypographyComponent>
          )}
          {inputData && (
            <CustomTypographyComponent variant="h5">
              Registratie formulier voor klant
            </CustomTypographyComponent>
          )}
        </Stack>
        <Stack
          width="100%"
          alignItems="center"
          spacing={2}
          padding={1}
          direction={"column"}
        >
          <FormControl sx={{ width: "100%" }} variant="outlined">
            <CustomTextField
              id="outlined-organization"
              label={"Naam organisatie"}
              controldisabled={false}
              required
              value={values.organization}
              onChange={handleChange("organization")}
            />
            {formIsSubmitted && (
              <FormHelperText
                sx={{
                  color: formErrors.organization ? theme.appColors.error : "",
                }}
                id="filled-organization-helper-text"
              >
                {formErrors.organization ? formErrors.organization : ""}
              </FormHelperText>
            )}
          </FormControl>
          <Stack direction={"row"} spacing={2} width="100%">
            <FormControl sx={{ width: "70%" }} variant="outlined">
              <CustomTextField
                id="outlined-street"
                label={"Straat"}
                controldisabled={false}
                required
                value={values.street}
                onChange={handleChange("street")}
              />
              {formIsSubmitted && (
                <FormHelperText
                  sx={{
                    color: formErrors.street ? theme.appColors.error : "",
                  }}
                  id="filled-street-helper-text"
                >
                  {formErrors.street ? formErrors.street : ""}
                </FormHelperText>
              )}
            </FormControl>
            <FormControl sx={{ width: "30%" }} variant="outlined">
              <CustomTextField
                id="outlined-number"
                label={"Huisnummer"}
                controldisabled={false}
                required
                value={values.number}
                onChange={handleChange("number")}
              />
              {formIsSubmitted && (
                <FormHelperText
                  sx={{
                    color: formErrors.number ? theme.appColors.error : "",
                  }}
                  id="filled-number-helper-text"
                >
                  {formErrors.number ? formErrors.number : ""}
                </FormHelperText>
              )}
            </FormControl>
          </Stack>
          <Stack direction={"row"} spacing={2} width="100%">
            <FormControl sx={{ width: "30%" }} variant="outlined">
              <CustomTextField
                id="outlined-postalCode"
                label={"Postcode"}
                controldisabled={false}
                required
                value={values.postalCode}
                onChange={handleChange("postalCode")}
              />
              {formIsSubmitted && (
                <FormHelperText
                  sx={{
                    color: formErrors.postalCode ? theme.appColors.error : "",
                  }}
                  id="filled-number-helper-text"
                >
                  {formErrors.postalCode ? formErrors.postalCode : ""}
                </FormHelperText>
              )}
            </FormControl>
            <FormControl sx={{ width: "70%" }} variant="outlined">
              <CustomTextField
                id="outlined-city"
                label={"Plaats"}
                controldisabled={false}
                required
                value={values.city}
                onChange={handleChange("city")}
              />
              {formIsSubmitted && (
                <FormHelperText
                  sx={{
                    color: formErrors.city ? theme.appColors.error : "",
                  }}
                  id="filled-city-helper-text"
                >
                  {formErrors.city ? formErrors.city : ""}
                </FormHelperText>
              )}
            </FormControl>
          </Stack>
          <FormControl sx={{ width: "100%" }} variant="outlined">
            <CustomTextField
              id="outlined-emailAddressInvoice"
              label={"E-mailadres factuur"}
              controldisabled={false}
              required
              value={values.emailAddressInvoice}
              onChange={handleChange("emailAddressInvoice")}
            />
            {formIsSubmitted && (
              <FormHelperText
                sx={{
                  color: formErrors.emailAddressInvoice
                    ? theme.appColors.error
                    : "",
                }}
                id="filled-number-helper-text"
              >
                {formErrors.emailAddressInvoice
                  ? formErrors.emailAddressInvoice
                  : ""}
              </FormHelperText>
            )}
          </FormControl>
          <FormControl sx={{ width: "100%" }} variant="outlined">
            <CustomTextField
              id="outlined-phoneNumber"
              label={"Telefoonnummer"}
              controldisabled={false}
              required
              value={values.phoneNumber}
              onChange={handleChange("phoneNumber")}
            />
            {formIsSubmitted && (
              <FormHelperText
                sx={{
                  color: formErrors.phoneNumber ? theme.appColors.error : "",
                }}
                id="filled-phoneNumber-helper-text"
              >
                {formErrors.phoneNumber ? formErrors.phoneNumber : ""}
              </FormHelperText>
            )}
          </FormControl>
        </Stack>
        <Stack>
          <CustomTypographyComponent variant="h5">
            Account gegevens
          </CustomTypographyComponent>
        </Stack>
        <Stack
          width="100%"
          alignItems="center"
          spacing={2}
          padding={1}
          direction={"column"}
        >
          <FormControl sx={{ width: "100%" }} variant="outlined">
            <CustomTextField
              id="outlined-firstName"
              label={"Voornaam"}
              controldisabled={false}
              required
              value={values.firstName}
              onChange={handleChange("firstName")}
            />
            {formIsSubmitted && (
              <FormHelperText
                sx={{
                  color: formErrors.firstName ? theme.appColors.error : "",
                }}
                id="filled-firstName-helper-text"
              >
                {formErrors.firstName ? formErrors.firstName : ""}
              </FormHelperText>
            )}
          </FormControl>
          <Stack direction={"row"} spacing={2} width="100%">
            <FormControl sx={{ width: "30%" }} variant="outlined">
              <CustomTextField
                id="outlined-infix"
                label={"Tussenv."}
                controldisabled={false}
                value={values.infix}
                onChange={handleChange("infix")}
              />
            </FormControl>
            <FormControl sx={{ width: "70%" }} variant="outlined">
              <CustomTextField
                id="outlined-lastName"
                label={"Achternaam"}
                controldisabled={false}
                required
                value={values.lastName}
                onChange={handleChange("lastName")}
              />
              {formIsSubmitted && (
                <FormHelperText
                  sx={{
                    color: formErrors.lastName ? theme.appColors.error : "",
                  }}
                  id="filled-lastName-helper-text"
                >
                  {formErrors.lastName ? formErrors.lastName : ""}
                </FormHelperText>
              )}
            </FormControl>
          </Stack>
          <FormControl sx={{ width: "100%" }} variant="outlined">
            <CustomTextField
              id="outlined-emailAddress"
              label={"E-mailadres"}
              controldisabled={false}
              required
              value={values.emailAddress}
              onChange={handleChange("emailAddress")}
            />
            {formIsSubmitted && (
              <FormHelperText
                sx={{
                  color: formErrors.emailAddress ? theme.appColors.error : "",
                }}
                id="filled-emailAddress-helper-text"
              >
                {formErrors.emailAddress ? formErrors.emailAddress : ""}
              </FormHelperText>
            )}
          </FormControl>
        </Stack>
        <Divider />
        <Stack spacing={2}>
          <FormControl sx={{ width: "100%" }} variant="outlined">
            <CustomTextField
              id="outlined-purchaseNumber"
              label={"Inkoopnummer"}
              controldisabled={false}
              value={values.purchaseNumber}
              onChange={handleChange("purchaseNumber")}
            />
          </FormControl>
          <FormControl sx={{ width: "100%" }} variant="outlined">
            <CustomTextField
              id="outlined-reference"
              label={" hoe bent u bij ons terecht gekomen ?"}
              controldisabled={false}
              value={values.reference}
              onChange={handleChange("reference")}
            />
          </FormControl>
        </Stack>

        {snackBar && (
          <SnackBar
            openState={snackBar}
            //@ts-ignore
            severity={severity}
            message={snackBarMessage}
            //@ts-ignore
            onClose={handleClose}
          ></SnackBar>
        )}
      </Stack>
    );
  }
);

RegistrationForm.displayName = "RegistrationForm";

export default RegistrationForm;
